// Plain-language definitions for ANANSI's domain terms, written for a
// non-engineer (CS, onboarding, ops). The single source of truth behind the
// <InfoTip> ⓘ affordance and the /glossary page. Part of the "ANANSI,
// approachable" foundation (sub-project A). Keep each definition short
// (≈ one sentence) and jargon-free — a new term shipped to the UI should get
// an entry here.

export interface GlossaryEntry {
  /** Display term, e.g. "Pipeline". */
  term: string;
  /** One-sentence, non-technical definition. */
  definition: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  pipeline: {
    term: "Pipeline",
    definition:
      "An automatic sync that copies records from one connected app to another whenever they change.",
  },
  connection: {
    term: "Connection",
    definition:
      "One connected app for a client — a HubSpot portal, a Jump environment, a data warehouse. Pipelines move data between connections.",
  },
  run: {
    term: "Run",
    definition:
      "A single record crossing a pipeline — one fan, order, or contact being synced. Each run shows whether it succeeded.",
  },
  client: {
    term: "Client",
    definition:
      "One Jump customer — a team, venue or league. Every connection, pipeline and run belongs to one client.",
  },
  activity: {
    term: "Activity",
    definition:
      "Every record that moved through a pipeline, newest first — one row per fan, order or contact, with whether it synced.",
  },
  needsAttention: {
    term: "Needs attention",
    definition:
      "Events Anansi could not process after several tries, set aside for a person to resend once the cause is fixed, or to discard.",
  },
  stuckEvent: {
    term: "Stuck event",
    definition:
      "One event waiting under Needs attention. It will not move until someone resends or discards it.",
  },
  synced: {
    term: "Synced",
    definition: "The record reached the destination and matches what the source sent.",
  },
  transform: {
    term: "Transform",
    definition:
      "The mapping a pipeline applies as it syncs — which fields from the source become which fields in the destination.",
  },
  environment: {
    term: "Environment",
    definition:
      "A label that scopes a pipeline to a client or stage (for example dev, staging, production). It tags data; it is not a separate deployment.",
  },
  environmentIsolation: {
    term: "Environment isolation",
    definition:
      "A safety rule: an event is only handled by pipelines whose environment matches the account it came from — so production data never flows into a staging pipeline, and the reverse.",
  },
  source: {
    term: "Source",
    definition: "Where a pipeline reads records from — the app that changed.",
  },
  destination: {
    term: "Destination",
    definition: "Where a pipeline writes records to — the app being kept up to date.",
  },
  ingest: {
    term: "Ingest",
    definition:
      "How a pipeline finds out about changes — either the moment they happen, or by checking the source on a regular schedule.",
  },
  conflictPolicy: {
    term: "Conflict policy",
    definition:
      "Who wins when a field has a value on both sides. “Sending side wins” replaces what is there; “receiving side wins” keeps it and only fills fields that are empty — so an already-filled field stops updating.",
  },
  secret: {
    term: "Secret",
    definition:
      "A stored credential (an API key or token) a connection uses to reach an outside app. Kept encrypted and never shown in full.",
  },
  portal: {
    term: "Portal",
    definition: "A client's individual HubSpot account, identified by its portal ID.",
  },
  upsert: {
    term: "Upsert",
    definition:
      "Update the matching record if it already exists, otherwise create it — so re-syncing never makes duplicates.",
  },
  dlq: {
    term: "Dead-letter queue (DLQ)",
    definition:
      "The engineering name for Needs attention — the holding area for stuck events, kept so nothing is lost.",
  },
  backfill: {
    term: "Backfill",
    definition:
      "A one-time catch-up sync of existing records, rather than only new changes from now on.",
  },
  "dead-letter": {
    term: "Dead-letter",
    definition:
      "The older name for Needs attention. A stuck event is one that could not be processed after several tries and is waiting for a person.",
  },
  refire: {
    term: "Refire",
    definition:
      "The older name for Resend — sending a stuck event through its pipeline again. Safe to do; it does not create a duplicate.",
  },
  "custom-sqs": {
    term: "Custom SQS destination",
    definition:
      "A destination that hands each record to Amazon SQS, an AWS service the client's own systems read from on their schedule.",
  },
  "managed-queue": {
    term: "Managed queue",
    definition:
      "An Amazon SQS destination Anansi creates and controls — including who is allowed to read from it — so it can be started or removed from this screen.",
  },
  dataTableLookup: {
    term: "Data-table lookup",
    definition:
      "Fill in a value by looking it up in a data table (a Snowflake view), matched on a shared key like an order number.",
  },
  profileLookup: {
    term: "Profile lookup",
    definition:
      "Fill in a value by reading it from the fan's profile record.",
  },
  requiredLookup: {
    term: "Required lookup",
    definition:
      "If the value can't be found, hold the message and retry rather than sending it without — use for values a downstream system can't do without.",
  },
  adoptedRecord: {
    term: "Adopted record",
    definition:
      "A record that already existed in the destination, which this pipeline now writes to instead of creating its own.",
  },
  recordIdSource: {
    term: "Record id source",
    definition:
      "Where the pipeline finds the id of the existing record — a field on the incoming event, or a lookup in your warehouse.",
  },
  writeMask: {
    term: "Write mask",
    definition:
      "Which fields a pipeline may write for one kind of write. Unticking a field means it is never sent — not even to fill it in when it is empty. Separate from the conflict policy, which decides who wins for the fields that are sent.",
  },
  dealStage: {
    term: "Deal stage",
    definition:
      "Where a deal sits in a HubSpot sales pipeline — for example Closed Won or Closed Lost.",
  },
  orderStatus: {
    term: "Order status",
    definition:
      "What Jump says is happening to an order right now — confirmed, cancelled, and so on.",
  },
  itemStatus: {
    term: "Item status",
    definition:
      "What Jump says is happening to a single item on an order — one seat, or one piece of merchandise.",
  },
  deleteRecord: {
    term: "Delete the record",
    definition:
      "Removes the record from the connected app entirely — not just its link to another record. HubSpot keeps deleted records in the recycling bin for 90 days; restoring one is manual.",
  },
  dryRun: {
    term: "Dry run",
    definition:
      "The rules are saved but not in effect — Anansi records what each one would have done. The pipeline keeps behaving exactly as it does today, which includes anything it already does automatically.",
  },
};

// Valid glossary keys — use this for props/args so a typo'd term is caught at
// build time rather than silently failing closed at runtime.
export type GlossaryKey = keyof typeof GLOSSARY;

/**
 * One glossary definition, or undefined when the key is unknown.
 *
 * Takes a wider `string` than `GlossaryKey` so a runtime caller stays safe;
 * typed call sites should pass the key type. The own-property check is what
 * keeps `__proto__` and `constructor` returning undefined rather than an
 * inherited object that would then be rendered as a definition.
 */
export function glossaryEntry(key: string): GlossaryEntry | undefined {
  return Object.prototype.hasOwnProperty.call(GLOSSARY, key)
    ? GLOSSARY[key as GlossaryKey]
    : undefined;
}
